import type {
  StyleBackgroundLayer,
  StyleBoxSides,
  StyleCornerSides,
  StyleProperties,
  StyleValue,
} from './types';

const boxSidesToValue = (sides?: StyleBoxSides): string | undefined => {
  if (!sides) return undefined;
  const { top, right, bottom, left } = sides;
  if ([top, right, bottom, left].every((side) => side == null || side === '')) return undefined;
  return [top, right, bottom, left].map((side) => side || '0').join(' ');
};

const cornerSidesToValue = (corners?: StyleCornerSides): string | undefined => {
  if (!corners) return undefined;
  const { topLeft, topRight, bottomRight, bottomLeft } = corners;
  if ([topLeft, topRight, bottomRight, bottomLeft].every((corner) => corner == null || corner === ''))
    return undefined;
  return [topLeft, topRight, bottomRight, bottomLeft].map((corner) => corner || '0').join(' ');
};

const stopsToValue = (stops: { color: string; at: number }[]): string =>
  stops
    .slice()
    .sort((a, b) => a.at - b.at)
    .map((stop) => `${stop.color} ${stop.at}%`)
    .join(', ');

const layerToImage = (layer: StyleBackgroundLayer): string => {
  switch (layer.kind) {
    case 'color':
      return `linear-gradient(${layer.color}, ${layer.color})`;
    case 'image':
      return `url("${layer.url}")`;
    case 'linear':
      return `linear-gradient(${layer.angle}deg, ${stopsToValue(layer.stops)})`;
    case 'radial':
      return `radial-gradient(${layer.shape} at ${layer.position || 'center'}, ${stopsToValue(layer.stops)})`;
  }
};

const layerToSize = (layer: StyleBackgroundLayer): string =>
  layer.kind === 'image' ? layer.size || 'auto' : 'auto';

const layerToPosition = (layer: StyleBackgroundLayer): string =>
  layer.kind === 'image' ? layer.position || '0% 0%' : 'center';

const layerToRepeat = (layer: StyleBackgroundLayer): string =>
  layer.kind === 'image' ? layer.repeat || 'repeat' : 'no-repeat';

const propertiesToVars = (
  prefix: string,
  properties?: StyleProperties
): Record<string, string> => {
  if (!properties) return {};
  const vars: Record<string, string> = {};

  const margin = boxSidesToValue(properties.margin);
  if (margin) vars[`--cs-${prefix}-margin`] = margin;

  const padding = boxSidesToValue(properties.padding);
  if (padding) vars[`--cs-${prefix}-padding`] = padding;

  const borderRadius = cornerSidesToValue(properties.borderRadius);
  if (borderRadius) vars[`--cs-${prefix}-radius`] = borderRadius;

  if (properties.backgroundColor) {
    vars[`--cs-${prefix}-bg-color`] = properties.backgroundColor;
  }

  const layers = properties.backgroundLayers?.filter(Boolean) ?? [];
  if (layers.length > 0) {
    vars[`--cs-${prefix}-bg-image`] = layers.map(layerToImage).join(', ');
    vars[`--cs-${prefix}-bg-size`] = layers.map(layerToSize).join(', ');
    vars[`--cs-${prefix}-bg-position`] = layers.map(layerToPosition).join(', ');
    vars[`--cs-${prefix}-bg-repeat`] = layers.map(layerToRepeat).join(', ');
  }

  return vars;
};

export const styleToCssVars = (value?: StyleValue): Record<string, string> => ({
  ...propertiesToVars('m', value?.mobile),
  ...propertiesToVars('d', value?.desktop),
});
